import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { type DocumentType } from '@/features/documents/DocumentType'
import { DocumentCreationItemViewModel } from '@/features/documents/DocumentCreationModal/DocumentCreationItem/DocumentCreationItemViewModel'
import { type Job } from '@/features/jobs/Job'
import { type UploadBarViewModel } from '@/features/documents/UploadBarViewModel'
import type { UploadDocumentOptions } from '@/features/documents/DocumentStore'
import { Id } from '@/infra/id'

/**
 * View model for choosing the types of files before uploading them to a job.
 */
export class DocumentCreationModalViewModel {
  readonly modal = new ModalViewModel()

  @observable
  accessor documentCreationItemVms: DocumentCreationItemViewModel[] = []

  @observable accessor dragging = false

  constructor(
    files: File[],
    private readonly _getDocumentTypes: () => DocumentType[],
    readonly job: Job,
    readonly uploadBarVm: UploadBarViewModel,
  ) {
    this.documentCreationItemVms = files.map(
      (file) => new DocumentCreationItemViewModel(file, _getDocumentTypes),
    )
  }

  /**
   * Whether there are files and each has a type.
   */
  @computed
  get canUpload() {
    return (
      this.documentCreationItemVms.length > 0 &&
      this.documentCreationItemVms.every((vm) => vm.selectedType !== null)
    )
  }

  @action.bound
  setDragging() {
    this.dragging = true
  }

  @action.bound
  stopDragging() {
    this.dragging = false
  }

  @action.bound
  removeFile(index: number) {
    this.documentCreationItemVms.splice(index, 1)
  }

  @action.bound
  clearFiles() {
    this.documentCreationItemVms = []
  }

  @action.bound
  addFiles(files: FileList) {
    Array.from(files).forEach((file) =>
      this.documentCreationItemVms.push(
        new DocumentCreationItemViewModel(file, this._getDocumentTypes),
      ),
    )
  }

  /**
   * Uploads the files. Never rejects; the upload bar alerts about each file
   * that fails.
   */
  uploadFiles() {
    const files: UploadDocumentOptions[] = this.documentCreationItemVms.map(
      (vm) => ({
        file: vm.file,
        workspaceId: Id.assert(this.job.workspace.id),
        jobId: Id.assert(this.job.id),
        documentTypeId: vm.selectedType
          ? Id.assert(vm.selectedType.id)
          : undefined,
      }),
    )

    return this.uploadBarVm.uploadFiles(files)
  }

  /**
   * Uploads the files and closes the modal once they are all uploaded.
   */
  upload = task.resolved(async () => {
    await this.uploadFiles()
    this.modal.close()
  })
}
